import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Menu,
  MenuItem,
  Divider,
} from "@mui/material";
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import Header from "../components/Admin/header/Header";
import { ArrowLeft } from "lucide-react";
import CloseIcon from "@mui/icons-material/Close";
import { DatePicker } from "@mui/x-date-pickers";
import { toast } from "react-toastify";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { EditIcon, ShareIcon } from "../comps/CustomIcons";
import { getPackageCountApi, exportPackageCountApi, exportPackageCountPDF } from "../services/reports.service";
import { getUserApi } from "../services/user.service";
import { useGetApi } from "../hooks/useGetApi";
import { DEFAULT_ROWS_PER_PAGE_OPTIONS } from "../utils/constants";

const iconStyleProps = {
  backgroundColor: "rgba(240, 247, 252, 1)",
  width: "44px",
  height: "44px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "8px",
  cursor: "pointer",
  transition: "background-color 0.3s ease",
};

// Proper dropdown options
const templateOptions = [
  { label: "All IPD Packages", value: "all_ipd_packages" },
  { label: "Monthly Package Count", value: "monthly_package_count" },
  { label: "Package Summary", value: "package_summary" },
  { label: "Package Utilization", value: "package_utilization" },
];

const typeOptions = [
  { label: "IPD", value: "ipd" },
  { label: "Emergency", value: "emergency" },
  { label: "Day Care", value: "day_care" },
  { label: "OPD Package", value: "opd_package" },
];

const menuItemStyle = {
  fontSize: "16px",
  fontWeight: "500",
  padding: "12px 16px",
};

const menuItems = [
  "Excel",
  "PDF",
  "Report Mail Send Scheduler",
  "Report Scheduler",
];

const headLabel = [
  { id: 1, label: "Sr. No.", value: "srNo" },
  { id: 2, label: "Month", value: "month" },
  { id: 3, label: "Package Name", value: "packageName" },
  { id: 4, label: "Package Count", value: "packageCount" },
  { id: 5, label: "Total Amount", value: "totalAmount" },
  { id: 6, label: "Doctor Reference", value: "doctorReference" },
];

export default function ReportsPackageCountSTatementIPD() {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    searchTemplate: null,
    startDate: null,
    endDate: null,
    type: null,
    drReferenceName: null,
  });

  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [errors, setErrors] = useState([]); // For required field validation errors
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE_OPTIONS[0]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  
  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState({
    srNo: true,
    month: true,
    packageName: true,
    packageCount: true,
    totalAmount: true,
    doctorReference: true,
  });

  // Menu anchor states
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);
  const filterMenuOpen = Boolean(filterAnchorEl);
  const downloadMenuOpen = Boolean(downloadAnchorEl);
  
  // Fetch doctors using useGetApi hook
  const doctorApiResult = useGetApi({
    apiFunction: getUserApi,
    params: { limit: 1000 },
  });
  
  // Extract doctors data and loading state
  const doctorsData = doctorApiResult.dataList;
  const doctorsLoading = doctorApiResult.isLoading;
  
  // Memoize doctor options from API data
  const doctorOptions = useMemo(() => {
    if (!doctorsData || !Array.isArray(doctorsData)) {
      return [];
    }
    
    return doctorsData
      .filter(user => {
        const role = user.role;
        if (!role) {
          const nameContainsDoctor = (user.firstName + ' ' + user.lastName).toLowerCase().includes('dr');
          return nameContainsDoctor;
        }
        
        const roleName = role.role || role.roleName || role.title || role.name || role.type || '';
        const roleNameLower = roleName.toLowerCase();
        
        const isDoctorRole = roleNameLower.includes('doctor') || 
                           roleNameLower.includes('physician') || 
                           roleNameLower.includes('dr') ||
                           roleNameLower === 'doctor' ||
                           user.isDoctor === true ||
                           user.userType === 'doctor';
        
        return isDoctorRole;
      })
      .map(doctor => ({
        label: `${doctor.firstName || ''} ${doctor.lastName || ''}`.trim() || doctor.email || 'Unknown Doctor',
        value: doctor._id
      }));
  }, [doctorsData]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Fetch report data - accepts optional parameters to avoid stale closure issues
  const fetchReportData = async (currentPage = page, currentRowsPerPage = rowsPerPage, showToast = false) => {
    try {
      setIsLoading(true);
      
      // Format dates for API call
      let startDateFormatted, endDateFormatted;
      
      try {
        if (formData.startDate) {
          const startDate = formData.startDate instanceof Date ? formData.startDate : new Date(formData.startDate);
          if (!isNaN(startDate.getTime())) {
            startDateFormatted = format(startDate, 'yyyy-MM-dd');
          } else {
            throw new Error('Invalid start date');
          }
        } else {
          startDateFormatted = null;
        }
        
        if (formData.endDate) {
          const endDate = formData.endDate instanceof Date ? formData.endDate : new Date(formData.endDate);
          if (!isNaN(endDate.getTime())) {
            endDateFormatted = format(endDate, 'yyyy-MM-dd');
          } else {
            throw new Error('Invalid end date');
          }
        } else {
          endDateFormatted = null;
        }
      } catch (dateError) {
        console.error("Date formatting error:", dateError);
        toast.error('Error formatting dates. Please check your date selections.');
        setIsLoading(false);
        return;
      }
      
      const params = {
        searchTemplate: formData.searchTemplate?.value,
        startDate: startDateFormatted,
        endDate: endDateFormatted,
        type: formData.type?.value,
        drReferenceName: formData.drReferenceName?.value,
        page: currentPage + 1, // API is 1-based, MUI is 0-based
        limit: currentRowsPerPage,
      };

      // Remove undefined/empty values
      Object.keys(params).forEach(key => {
        if (params[key] === undefined || params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });

      const response = await getPackageCountApi(params);
      
      if (response?.data) {
        // Ensure we always get an array
        let dataArray = [];
        if (Array.isArray(response.data)) {
          dataArray = response.data;
        } else if (response.data.records && Array.isArray(response.data.records)) {
          dataArray = response.data.records;
        } else if (response.data.packages && Array.isArray(response.data.packages)) {
          dataArray = response.data.packages;
        } else {
          // If no array found, create empty array
          dataArray = [];
        }
        
        setTableData(dataArray);
        setTotalCount(response.data.totalRecords || response.data.total || dataArray.length || 0);
        setPage(currentPage);
        setRowsPerPage(currentRowsPerPage);
        
        if (showToast && dataArray.length > 0) {
          toast.success(`Found ${dataArray.length} records`);
        }
      } else {
        setTableData([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching report data:', error);
      toast.error("Failed to fetch report data");
      setTableData([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    
    // Validate required fields and collect error field names
    const errorFields = [];
    if (!formData.startDate) {
      errorFields.push('startDate');
    }
    if (!formData.endDate) {
      errorFields.push('endDate');
    }
    
    if (errorFields.length > 0) {
      setErrors(errorFields);
      toast.error("Please fill all the required fields.");
      return;
    }
    
    // Clear errors if validation passes
    setErrors([]);
    setPage(0);
    setHasSearched(true);
    fetchReportData(0, rowsPerPage, true);
  };
  
  // Handle filter icon click (column visibility)
  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  // Toggle column visibility
  const toggleColumn = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  // Handle share functionality
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Month Wise Package Count Statement (IPD)',
        text: `Month Wise Package Count Statement (IPD) - ${totalCount || 0} records`,
        url: window.location.href,
      }).catch((error) => {
        console.log('Error sharing:', error);
      });
    } else {
      // Fallback: Copy URL to clipboard
      navigator.clipboard.writeText(window.location.href).then(() => {
        toast.success('Report URL copied to clipboard');
      }).catch(() => {
        toast.error('Failed to copy URL');
      });
    }
  };

  // Handle download menu
  const handleDownloadClick = (event) => {
    setDownloadAnchorEl(event.currentTarget);
  };

  const handleDownloadClose = () => {
    setDownloadAnchorEl(null);
  };

  // Handle menu item clicks
  const handleMenuClick = (item) => {
    handleDownloadClose();
    switch (item) {
      case 'Excel':
        handleExport();
        break;
      case 'PDF':
        handlePDFExport();
        break;
      case 'Report Mail Send Scheduler':
        toast.info('Report Mail Send Scheduler feature coming soon');
        break;
      case 'Report Scheduler':
        toast.info('Report Scheduler feature coming soon');
        break;
      default:
        break;
    }
  };
  
  // Handle PDF export
  const handlePDFExport = async () => {
    try {
      setIsExporting(true);
      
      // Format dates for API call
      let startDateFormatted, endDateFormatted;
      
      try {
        if (formData.startDate) {
          const startDate = formData.startDate instanceof Date ? formData.startDate : new Date(formData.startDate);
          if (!isNaN(startDate.getTime())) {
            startDateFormatted = format(startDate, 'yyyy-MM-dd');
          }
        }
        
        if (formData.endDate) {
          const endDate = formData.endDate instanceof Date ? formData.endDate : new Date(formData.endDate);
          if (!isNaN(endDate.getTime())) {
            endDateFormatted = format(endDate, 'yyyy-MM-dd');
          }
        }
      } catch (dateError) {
        console.error("Date formatting error:", dateError);
        toast.error('Error formatting dates. Please check your date selections.');
        return;
      }
      
      const apiParams = {
        searchTemplate: formData.searchTemplate?.value,
        startDate: startDateFormatted,
        endDate: endDateFormatted,
        type: formData.type?.value,
        drReferenceName: formData.drReferenceName?.value,
        page: 1,
        limit: 100000, // Export all records
      };

      // Remove undefined/empty values
      Object.keys(apiParams).forEach(key => {
        if (apiParams[key] === undefined || apiParams[key] === '' || apiParams[key] === null) {
          delete apiParams[key];
        }
      });

      // Define columns for PDF export
      const allColumns = headLabel.map(col => ({
        header: col.label,
        dataKey: col.value,
        visibleKey: col.value
      }));
      
      // Filter columns based on visibleColumns state
      const columns = allColumns.filter(col => visibleColumns[col.visibleKey]).map(({ visibleKey, ...col }) => col);

      // Prepare date range for PDF header
      const dateRange = formData.startDate && formData.endDate ? {
        startDate: formData.startDate.toISOString(),
        endDate: formData.endDate.toISOString(),
      } : null;

      const result = await exportPackageCountPDF(apiParams, columns, dateRange);
      
      if (result.success) {
        toast.success(`Successfully exported ${result.count} records to PDF!`);
      } else {
        throw new Error('PDF export failed');
      }
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error(`PDF export failed: ${error.message || 'Unknown error'}`);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      
      // Format dates for API call
      let startDateFormatted, endDateFormatted;
      
      try {
        if (formData.startDate) {
          const startDate = formData.startDate instanceof Date ? formData.startDate : new Date(formData.startDate);
          if (!isNaN(startDate.getTime())) {
            startDateFormatted = format(startDate, 'yyyy-MM-dd');
          }
        }
        
        if (formData.endDate) {
          const endDate = formData.endDate instanceof Date ? formData.endDate : new Date(formData.endDate);
          if (!isNaN(endDate.getTime())) {
            endDateFormatted = format(endDate, 'yyyy-MM-dd');
          }
        }
      } catch (dateError) {
        console.error("Date formatting error:", dateError);
        toast.error('Error formatting dates. Please check your date selections.');
        return;
      }
      
      const exportParams = {
        searchTemplate: formData.searchTemplate?.value,
        startDate: startDateFormatted,
        endDate: endDateFormatted,
        type: formData.type?.value,
        drReferenceName: formData.drReferenceName?.value,
        page: 1,
        limit: 100000, // Export all records
      };

      // Remove undefined/empty values
      Object.keys(exportParams).forEach(key => {
        if (exportParams[key] === undefined || exportParams[key] === '' || exportParams[key] === null) {
          delete exportParams[key];
        }
      });

      // Define column order for Excel export
      const columnOrder = headLabel.filter(col => visibleColumns[col.value]).map(col => col.value);

      const result = await exportPackageCountApi(exportParams, columnOrder);
      
      if (result.success) {
        toast.success(`Successfully exported ${result.count} records to Excel!`);
      } else {
        throw new Error('Export failed');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error(`Export failed: ${error.message || 'Unknown error'}`);
    } finally {
      setIsExporting(false);
    }
  };

  // Handle pagination
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing rows per page
  };
  
  // Refetch data when pagination changes (only if user has already searched)
  useEffect(() => {
    if (hasSearched) {
      fetchReportData(page, rowsPerPage);
    }
  }, [page, rowsPerPage]);

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        <Box sx={{ width: "100%", pr: "90px" }}>
          <Box sx={{ my: "20px" }}>
            <Button
              startIcon={<ArrowLeft />}
              onClick={handleBack}
              sx={{
                color: "#5D6A82",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: "bold",
                padding: 0,
                transition: "none !important",
                marginBottom: "16px",
              }}
            >
              Back
            </Button>
            <Typography sx={{
              fontFamily: "Open Sans",
              fontWeight: 600,
              fontSize: "20px",
              lineHeight: "29px",
              letterSpacing: "0px",
              color: "#5D6A82",
              marginBottom: "1rem",
            }}>
              Month Wise Package Count Statement (IPD)
            </Typography>
          </Box>
          <Box>
            <Grid spacing={2} container>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={templateOptions || []}
                  getOptionLabel={(option) => option?.label || ""}
                  value={formData.searchTemplate}
                  onChange={(event, newValue) => handleInputChange('searchTemplate', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search Template"
                      name="searchTemplate"
                      fullWidth
                    />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      name="startDate"
                      label="Start Date"
                      value={formData.startDate}
                      onChange={(newValue) => {
                        handleInputChange('startDate', newValue);
                        // Clear error when field is changed
                        if (errors.includes('startDate')) {
                          setErrors(errors.filter(e => e !== 'startDate'));
                        }
                      }}
                      slotProps={{
                        textField: { 
                          required: true, 
                          fullWidth: true,
                          InputLabelProps: { required: true, sx: { '& .MuiFormLabel-asterisk': { color: 'red' } } }
                        },
                      }}
                      sx={{ 
                        bgcolor: "white",
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: errors?.includes('startDate') ? '1px solid red' : '1px solid inherit',
                          },
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      name="endDate"
                      label="End Date"
                      value={formData.endDate}
                      onChange={(newValue) => {
                        handleInputChange('endDate', newValue);
                        // Clear error when field is changed
                        if (errors.includes('endDate')) {
                          setErrors(errors.filter(e => e !== 'endDate'));
                        }
                      }}
                      slotProps={{
                        textField: { 
                          required: true, 
                          fullWidth: true,
                          InputLabelProps: { required: true, sx: { '& .MuiFormLabel-asterisk': { color: 'red' } } }
                        },
                      }}
                      sx={{ 
                        bgcolor: "white",
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: errors?.includes('endDate') ? '1px solid red' : '1px solid inherit',
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={typeOptions || []}
                  getOptionLabel={(option) => option?.label || ""}
                  value={formData.type}
                  onChange={(event, newValue) => handleInputChange('type', newValue)}
                  renderInput={(params) => (
                    <TextField {...params} label="Type" name="type" fullWidth />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={doctorOptions || []}
                  getOptionLabel={(option) => option?.label || ""}
                  value={formData.drReferenceName}
                  onChange={(event, newValue) => handleInputChange('drReferenceName', newValue)}
                  loading={doctorsLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Dr. Reference Name"
                      name="drReferenceName"
                      fullWidth
                    />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
            </Grid>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: "20px",
              gap: 2,
            }}
          >
            <Button
              variant="contained"
              color="secondary"
              onClick={handleExport}
              disabled={isExporting}
              sx={{ minWidth: 120 }}
            >
              {isExporting ? <CircularProgress size={20} /> : 'Export'}
            </Button>
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={isLoading}
              sx={{
                textTransform: "none",
                backgroundColor: "rgba(1, 121, 176, 1)",
                width: "150px",
              }}
            >
              {isLoading ? <CircularProgress size={20} /> : 'Search'}
            </Button>
          </Box>
          <Box
            sx={{
              border: "1px solid rgba(223, 232, 239, 1)",
              borderRadius: "8px",
              my: "20px",
              p: "20px",
            }}
          >
            {/* Header with Rows/Columns count and action icons */}
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: 1,
                  pb: "15px",
                }}
              >
                <Typography
                  sx={{ fontSize: "14px", color: "rgba(93, 106, 130, 1)" }}
                >
                  Rows: {totalCount || 0} | Columns:{" "}
                  {Object.values(visibleColumns).filter(v => v).length}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box sx={iconStyleProps} onClick={handleFilterClick}>
                    <EditIcon
                      sx={{
                        cursor: "pointer",
                        color: "transparent",
                        fontSize: "18px",
                      }}
                    />
                  </Box>
                  <Box sx={iconStyleProps} onClick={handleShare}>
                    <ShareIcon 
                      sx={{
                        cursor: "pointer",
                        fontSize: "18px",
                      }}
                    />
                  </Box>
                  <Box sx={iconStyleProps}>
                    <IconButton onClick={handleDownloadClick}>
                      <FileDownloadOutlinedIcon />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            </Box>
            <TableContainer component={Paper}>
              <Table aria-label="package count statement table" sx={{ minWidth: 800 }}>
                <TableHead
                  sx={{
                    backgroundColor: "#f5f5f5",
                    "& th": { fontWeight: "bold" },
                  }}
                >
                  <TableRow>
                    {headLabel?.map((option) => 
                      visibleColumns[option.value] && (
                        <TableCell
                          key={option?.id}
                          sx={{ textAlign: option?.align || "left" }}
                        >
                          {option?.label}
                        </TableCell>
                      )
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={Object.values(visibleColumns).filter(v => v).length} sx={{ textAlign: 'center', py: 4 }}>
                        <CircularProgress />
                        <Typography sx={{ mt: 2 }}>Loading report data...</Typography>
                      </TableCell>
                    </TableRow>
                  ) : !Array.isArray(tableData) || tableData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={Object.values(visibleColumns).filter(v => v).length} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="textSecondary">No data available. Please search with filters.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    tableData.map((row, rowIndex) => (
                      <TableRow key={row?._id || rowIndex}>
                        {headLabel?.map((column) => 
                          visibleColumns[column.value] && (
                            <TableCell key={column?.id}>
                              {column.value === 'srNo' 
                                ? (page * rowsPerPage + rowIndex + 1)
                                : (row?.[column?.value] || "-")
                              }
                            </TableCell>
                          )
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Pagination */}
            {totalCount > 0 && (
              <TablePagination
                rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            )}
            
            {/* Filter Menu */}
            <Menu
              anchorEl={filterAnchorEl}
              open={filterMenuOpen}
              onClose={handleFilterClose}
              PaperProps={{
                style: {
                  maxHeight: 400,
                  padding: "8px 0",
                  width: "200px",
                },
              }}
            >
              <MenuItem sx={{ ...menuItemStyle, fontWeight: "bold" }} disabled>
                Show/Hide Columns
              </MenuItem>
              <Divider />
              {headLabel.map((column) => (
                <MenuItem
                  key={column.id}
                  onClick={() => toggleColumn(column.value)}
                  sx={menuItemStyle}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>{column.label}</Box>
                    {visibleColumns[column.value] && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                  </Box>
                </MenuItem>
              ))}
            </Menu>
            
            {/* Download Menu */}
            <Menu
              anchorEl={downloadAnchorEl}
              open={downloadMenuOpen}
              onClose={handleDownloadClose}
              slotProps={{
                sx: {
                  borderRadius: "12px",
                  boxShadow: "0px 2px 10px rgba(0,0,0,0.1)",
                  padding: "8px 0",
                  width: "250px",
                },
              }}
            >
              {menuItems.map((item, index) => (
                <React.Fragment key={item}>
                  <MenuItem onClick={() => handleMenuClick(item)} sx={menuItemStyle}>
                    {item}
                  </MenuItem>
                  {index !== menuItems.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </Menu>
          </Box>
        </Box>
      </Box>
    </Container>
  );
}
